import {
  useEffect,
  useState } from "react";
import {
  parseParts,
  heroPartKey,
  heroBaseKey,
  heroLookKey,
  heroMedalKey,
  heroOverlayKeys,
  overlayFitOn,
  type OverlayFitDoc,
  usesPresetArt,
  DEFAULT_PARTS,
  type HeroParts,
  type PartCategory,
  type SavedHero,
  HERO_LAYER_ORDER,
} from "@greg-chick/shared";
import { paletteColors } from "../lib/heroPalette";
import { loadAssetManifest, assetManifestNow, type AssetManifest } from "../lib/assetManifest";
import { loadOverlayFit, onOverlayFitReady, resetOverlayFitCache } from "../lib/overlayFitApi";
import { subscribeContentUpdates } from "../lib/liveReload";
import { isRallyCompleted } from "../lib/rallyState";

// 레이어드 아바타: 카테고리별로 ①게시 이미지(hero.<cat>.<id>) 우선 → ②절차적 더미 폴백.
// 운영자가 관리자에서 파츠 이미지를 추가하면 코드 수정 없이 조합이 다양해진다(콘텐츠 주도).

// z-순서(뒤→앞)는 **shared 한 곳**(HERO_LAYER_ORDER) — 체험공간 캔버스와 같은 순서여야 한다.
const ZORDER: readonly PartCategory[] = HERO_LAYER_ORDER;

const BODY_W: Record<string, number> = { b1: 34, b2: 38, b3: 30, b4: 42 };
// 얼굴·머리모양은 파츠가 아니다(아이 그림에 포함) — 절차적 더미에서만 쓰는 고정값.
const HEAD_R = 18;

interface Props {
  hero: SavedHero;
  size?: number;
  idle?: boolean; // 천천히 까딱이는 idle(시각안전: 저속, 3Hz 미만)
  className?: string;
  medal?: boolean; // 완주 메달 표시 강제(기본: 랠리 완주 시 자동). 미리보기에서 끄고 싶을 때 false.
}

/**
 * 정수리 위에 비워 두는 여유(뷰박스 100 기준).
 * 숨쉬기 애니메이션 2.5px ≈ 1.8 + 모자가 프레임 위로 넘치는 몫 ≈ 2 + 눈으로 답답하지 않을 만큼.
 * 늘리면 같은 상자 안에서 아바타가 그만큼 작아 보이므로 필요한 만큼만 둔다.
 */
const HEAD_ROOM = 10;

export function HeroAvatar({ hero, size = 120, idle = true, className, medal }: Props) {
  // 기다리지 않는다 — 번들 정적 아트로 먼저 그리고, 게시본이 오면 그때 바뀐다.
  const [manifest, setManifest] = useState<AssetManifest | null>(assetManifestNow);
  const [fitDoc, setFitDoc] = useState<OverlayFitDoc>({});
  useEffect(() => {
    let alive = true;
    void loadAssetManifest().then((m) => alive && setManifest(m));
    // 운영자가 관리자에서 맞춘 모자·소품 자리. 못 받아도 코드 기본값으로 그린다.
    void loadOverlayFit().then((d) => alive && setFitDoc(d));
    // 관리자에서 자리를 저장하면 **이 화면도 바로** 따라간다 — 새로고침을 시켜서는 안 된다.
    // (운영자가 관리자에서 맞춘 그대로 메인화면에 보여야 "일관되게" 맞춘 게 된다)
    const off = subscribeContentUpdates((up) => {
      if (up.type !== "overlayfit") return;
      resetOverlayFitCache();
      void loadOverlayFit().then((d) => alive && setFitDoc(d));
    });
    // 첫 시도가 늦거나 실패했다가 **뒤늦게 도착한** 값도 받는다.
    // 이 화면은 뜰 때 한 번만 부르므로, 안 받으면 영영 기본값으로 남는다.
    const offReady = onOverlayFitReady((d) => alive && setFitDoc(d));
    return () => {
      alive = false;
      off();
      offReady();
    };
  }, []);

  const parts = parseParts(hero.parts) ?? DEFAULT_PARTS;
  const c = paletteColors(hero.palette);
  const bodyW = BODY_W[parts.body] ?? 34;
  const partUrl = (cat: PartCategory) => manifest?.assets[heroPartKey(cat, parts[cat])]?.url;
  // 렌더 우선순위: ①완성형 프리셋(hero.base.<id>) ②직접 꾸미기 통짜 룩(hero.look.<몸>_<옷>, 자연 착장)
  //  ③레이어드/절차적(폴백). ①②는 단일 이미지라 얹은 느낌 없이 자연스럽다.
  const isPreset = usesPresetArt(hero.base, (k) => !!manifest?.assets[k]?.url);
  const baseUrl = isPreset
    ? manifest?.assets[heroBaseKey(hero.base)]?.url
    : manifest?.assets[heroLookKey(parts.body, parts.outfit)]?.url;
  // 직접 꾸미기 룩 위에 얹는 모자·소품. 오버레이 그림은 룩과 같은 정사각 프레임에 그려져 있지만
  // **그림 속 물건의 크기·자리까지 맞는 건 아니다**(가방이 몸 옆에 떠 있었다) — shared 의
  // `overlayFit` 표가 키마다 배율·이동을 준다. 월드 캔버스도 같은 표를 본다.
  // 완성형 프리셋은 그 자체로 완성된 캐릭터 → 얹지 않는다.
  const overlays = isPreset
    ? []
    : heroOverlayKeys(parts)
        // 아이마다 머리 크기·턱 높이가 달라서 아이템 표 값만으로는 안 맞는다 —
        // `overlayFitOn` 이 그 아이(`b1`~`b4`)의 실측 기준점으로 옮겨 준다.
        // 여기는 정면 미리보기라 dir 은 항상 "down".
        .map((k) => ({ url: manifest?.assets[k]?.url, fit: overlayFitOn(k, "down", `${parts.body}_${parts.outfit}`, fitDoc) }))
        .filter((o): o is { url: string; fit: ReturnType<typeof overlayFitOn> } => !!o.url);

  // 완주 메달(FR-RALLY-10): 랠리 완주 시 자동 표시. medal prop 으로 강제 on/off 가능.
  // 모든 렌더 경로(프리셋·룩·절차적) 위 최상단 z에 얹으므로 어떤 아바타든 가슴에 걸린다.
  const showMedal = (medal ?? isRallyCompleted()) && !!manifest?.assets[heroMedalKey()]?.url;
  /**
   * 메달도 **모자·소품과 똑같이** 자리를 잡는다 — 아이별 가슴 높이 보정 + 운영자 조절값.
   *
   * 예전엔 `anchoredFit({scale:1,...})` 을 고정으로 쓰고 그린 뒤 `dx·dy` 만 반영했다.
   * 그래서 관리자에서 **크기를 조절해도 아무 일도 안 일어났다**(scale 을 아무도 안 봤다).
   * 게다가 조절값 문서(`fitDoc`)를 통째로 무시해서, 운영자가 뭘 하든 화면은 그대로였다.
   */
  const medalFit = overlayFitOn(heroMedalKey(), "down", `${parts.body}_${parts.outfit}`, fitDoc);
  const medalUrl = manifest?.assets[heroMedalKey()]?.url;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      /**
       * 🔴 **정수리 위를 비워 둔다.**
       *
       * 그림 좌표는 그대로(0~100)인데 보이는 틀만 위로 `HEAD_ROOM` 만큼 넓힌다. 그래서
       * 모자 자리(`overlayFit` 의 dx·dy·scale)는 **한 값도 안 바뀐다** — 운영자가 맞춰 둔 것을
       * 건드리지 않으면서 위쪽 여유만 생긴다.
       *
       * 왜 필요한가: 룩 그림은 정수리가 512px 중 y=6 에서 시작한다(거의 붙어 있다).
       * 거기에 ① 숨쉬기 애니메이션이 위로 2.5px 밀고 ② 모자는 원래 프레임 위로 넘치게
       * 설계돼 있다. 그래서 미리보기 상자 테두리에 머리가 닿아 **잘려 보인다**(2026-08-23 신고).
       *
       * `overflow: visible` 은 그대로 둔다 — 그래도 넘치는 그림이 있으면 자르지 않고 보여 준다.
       */
      viewBox={`0 ${-HEAD_ROOM} 100 ${100 + HEAD_ROOM}`}
      style={{ overflow: "visible" }}
      role="img"
      aria-label={`히어로 미리보기 (${hero.nickname ?? "꼬마 탐험가"})`}
    >
      <g className={idle ? "hero-avatar__idle" : undefined}>
        {baseUrl ? (
          <>
            <image href={baseUrl} x={0} y={0} width={100} height={100} preserveAspectRatio="xMidYMid meet" />
            {overlays.map(({ url, fit }) => {
              // 프레임 중심을 기준으로 키우고(scale) 옮긴다(dx·dy). 단위는 뷰박스 100 기준.
              const s = 100 * fit.scale;
              const cx = 50 + 100 * fit.dx;
              const cy = 50 + 100 * fit.dy;
              // 회전·반전은 **놓인 자리의 중심**을 축으로 — 축이 어긋나면 돌릴 때 물건이 날아간다.
              const t = [
                fit.rot ? `rotate(${fit.rot} ${cx} ${cy})` : "",
                fit.flipX ? `translate(${2 * cx} 0) scale(-1 1)` : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <image
                  key={url}
                  href={url}
                  x={cx - s / 2}
                  y={cy - s / 2}
                  width={s}
                  height={s}
                  transform={t || undefined}
                  preserveAspectRatio="xMidYMid meet"
                />
              );
            })}
          </>
        ) : (
          (() => {
            // 파츠 이미지(페이퍼돌)가 하나라도 있으면 "이미지 모드": 이미지 없는 카테고리는
            // 몸 이미지에 포함되므로 절차적으로 그리지 않는다(이중 그리기 방지). 아무 이미지도 없으면 전부 절차적.
            const anyPartImage = ZORDER.some((cat) => partUrl(cat));
            return ZORDER.map((cat) => {
              const url = partUrl(cat);
              if (url) {
                return <image key={cat} href={url} x={0} y={0} width={100} height={100} preserveAspectRatio="xMidYMid meet" />;
              }
              if (anyPartImage) return null;
              return <ProceduralPart key={cat} cat={cat} parts={parts} c={c} bodyW={bodyW} />;
            });
          })()
        )}
      </g>
      {/* 완주 메달 — idle 그룹 밖(같이 흔들리면 어색).
          그림은 모자·소품과 같은 512 정사각 프레임에 **제자리(가슴)** 로 그려져 있어서,
          다른 오버레이와 똑같이 "프레임 중심 기준 확대 → 이동" 하면 된다. */}
      {showMedal && medalUrl ? (
        (() => {
          const s = 100 * medalFit.scale;
          const cx = 50 + 100 * medalFit.dx;
          const cy = 50 + 100 * medalFit.dy;
          return (
            <image
              href={medalUrl}
              x={cx - s / 2}
              y={cy - s / 2}
              width={s}
              height={s}
              preserveAspectRatio="xMidYMid meet"
              aria-label="경주 완주 메달"
            />
          );
        })()
      ) : null}
    </svg>
  );
}

// ── 절차적 더미 파츠(이미지 없을 때) ───────────────────────────
function ProceduralPart({
  cat,
  parts,
  c,
  bodyW,
}: {
  cat: PartCategory;
  parts: HeroParts;
  c: { suit: string; skin: string; accent: string };
  bodyW: number;
}) {
  const headR = HEAD_R;
  const headCy = 36;
  switch (cat) {
    // 아이 하나 = 다리·몸통·머리·눈. 머리모양·표정은 파츠가 아니므로(아이 그림에 포함) 여기서 같이 그린다.
    case "body": {
      const ey = 37;
      return (
        <g>
          <rect x={42} y={74} width={6} height={16} rx={3} fill={c.suit} />
          <rect x={52} y={74} width={6} height={16} rx={3} fill={c.suit} />
          <rect x={50 - bodyW / 2} y={48} width={bodyW} height={30} rx={10} fill={c.suit} />
          <circle cx={50} cy={headCy} r={headR} fill={c.skin} />
          <g fill="#2a2320">
            <circle cx={45} cy={ey} r={2} />
            <circle cx={55} cy={ey} r={2} />
          </g>
        </g>
      );
    }
    case "outfit": // o0=없음
      if (parts.outfit === "o1") return <rect x={50 - bodyW / 2} y={48} width={bodyW} height={6} rx={3} fill={c.accent} />; // 목도리
      if (parts.outfit === "o2") return <rect x={50 - 3} y={48} width={6} height={30} fill={c.accent} opacity={0.85} />; // 줄무늬
      if (parts.outfit === "o3") return <polygon points={`${50 - bodyW / 2},50 ${50 + bodyW / 2},50 ${50 + bodyW / 2 + 4},78 ${50 - bodyW / 2 - 4},78`} fill={c.accent} opacity={0.5} />; // 망토
      return null;
    case "acc": // a0=없음
      if (parts.acc === "a1") return <rect x={50 - bodyW / 2 - 4} y={52} width={4} height={20} rx={2} fill={c.accent} />;
      if (parts.acc === "a2") return <circle cx={50} cy={62} r={6} fill={c.accent} />;
      if (parts.acc === "a3") return <polygon points="50,40 44,50 56,50" fill={c.accent} />;
      if (parts.acc === "a4")
        return (
          <g>
            <rect x={47} y={48} width={6} height={10} fill="#c0392b" />
            <circle cx={50} cy={62} r={7} fill="#f4c430" stroke="#b8860b" strokeWidth={1.5} />
            <text x={50} y={65} textAnchor="middle" fontSize="8" fill="#7a5a1e">★</text>
          </g>
        );
      return null;
    case "hair": { // c0=안 씀
      const top = headCy - headR;
      if (parts.hair === "c1") return <path d={`M${50 - headR} ${top + 4} q${headR} -10 ${headR * 2} 0`} fill="#5a3d28" />; // 앞머리
      if (parts.hair === "c2") return <rect x={50 - headR - 1} y={top} width={headR * 2 + 2} height={6} rx={3} fill={c.accent} />; // 모자
      if (parts.hair === "c3") return <g fill="#d6486d"><circle cx={50 - headR} cy={top + 4} r={3} /><circle cx={50 - headR + 4} cy={top + 4} r={3} /></g>; // 리본
      if (parts.hair === "c4") return <path d={`M${50 - headR} ${headCy} q-3 ${headR} 3 ${headR + 4} M${50 + headR} ${headCy} q3 ${headR} -3 ${headR + 4}`} stroke="#5a3d28" strokeWidth={4} fill="none" strokeLinecap="round" />; // 긴머리
      return null;
    }
    default:
      return null;
  }
}
